import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import poolsConfig from '~/config/constants/pools'
import {BIG_ZERO} from '~/utils/bigNumber'

import {PoolsState, CakeVault, VaultFees, AppThunk} from '~/state/types'
import {getPoolApr} from '~/utils/apr'
import {getBalanceNumber} from '~/utils/formatBalance'
import {fetchPoolsBlockLimits, fetchPoolsStakingLimits, fetchPoolsTotalStaking} from './fetchPools'
import {
  fetchPoolsAllowance,
  fetchUserBalances,
  fetchUserStakeBalances,
  fetchUserPendingRewards,
} from './fetchPoolsUser'
import {fetchPublicVaultData, fetchVaultFees} from './fetchVaultPublic'
// import fetchVaultUser from './fetchVaultUser'
import {getTokenPricesFromFarm, toSerializedPoolConfig} from './helpers'
import {GRAPHQL_ENDPOINTS} from "~/config/constants/endpoints";
import map from "lodash/map";
import {PoolDetails, PoolFeeDataContainer} from "~/state/clpools";
import {getPoolFeesData, PairDayData} from "~/state/pools/queries/poolFeesData";


const initPoolConfig = Object.keys(poolsConfig).reduce((accum, key) => {
  /* eslint-disable no-param-reassign */
  accum[key] = poolsConfig[key].map(pool => toSerializedPoolConfig(Number.parseInt(key), pool))
  return accum
}, {})


const initialState: PoolsState = {
  data: initPoolConfig,
  userDataLoaded: false,
  poolFees: {
    dataLoaded: false,
    byDate: {},
    pools: []
  },
  cakeVault: {
    totalShares: null,
    pricePerFullShare: null,
    totalCakeInVault: null,
    estimatedCakeBountyReward: null,
    totalPendingCakeHarvest: null,
    fees: {
      performanceFee: null,
      callFee: null,
      withdrawalFee: null,
      withdrawalFeePeriod: null,
    },
    userData: {
      isLoading: true,
      userShares: null,
      cakeAtLastUserAction: null,
      lastDepositedTime: null,
      lastUserActionTime: null,
    },
  },
}

// Thunks
export const fetchAllPoolFeesAsync = createAsyncThunk<PoolFeeDataContainer, { start: number, end: number, chainId: number }>(
  "pools/fetchAllPoolFeesPerChainAsync",
  async ({chainId, start, end}) => {
    const TOTAL_FEE = 0.003
    let poolFeesResult: Map<string, PairDayData[]> = new Map()

    const endpoint = GRAPHQL_ENDPOINTS[chainId]
    await getPoolFeesData(endpoint.metropolisV1InfoUrl, start, end, 1000, 0).then(result => {
      poolFeesResult[chainId] = result
    })
    await getPoolFeesData(endpoint.metropolisV1InfoUrl, start, end, 1000, 1000).then(result => {
      poolFeesResult[chainId] = poolFeesResult[chainId].concat(result)
    })
    await getPoolFeesData(endpoint.metropolisV1InfoUrl, start, end, 1000, 2000).then(result => {
      poolFeesResult[chainId] = poolFeesResult[chainId].concat(result)
    })
    await getPoolFeesData(endpoint.metropolisV1InfoUrl, start, end, 1000, 3000).then(result => {
      poolFeesResult[chainId] = poolFeesResult[chainId].concat(result)
    })
    await getPoolFeesData(endpoint.metropolisV1InfoUrl, start, end, 1000, 4000).then(result => {
      poolFeesResult[chainId] = poolFeesResult[chainId].concat(result)
    })

    const pools: Map<string, PoolDetails> = new Map()
    const poolFeeDataContainer: PoolFeeDataContainer = {error: false, data: []}
    const poolFeeData = {}
    if (poolFeesResult) {
      map(poolFeesResult[chainId], (pairDayData: PairDayData) => {
        const existingEntry = poolFeeData[pairDayData.date]
        pools.set(pairDayData.pairAddress, {
          chain: chainId.toString(),
          lbPairAddress: pairDayData.pairAddress,
          lbPairName: `${pairDayData.token0.name}-${pairDayData.token1.name}`,
        })
        if (existingEntry) {
          const updatedEntry = [
            ...existingEntry,
            {
              lbPairAddress: pairDayData.pairAddress,
              lbPairName: `${pairDayData.token0.name}-${pairDayData.token1.name}`,
              feeUSD: pairDayData.dailyVolumeUSD * TOTAL_FEE,
            }
          ]
          poolFeeData[pairDayData.date] = updatedEntry
        } else {
          poolFeeData[pairDayData.date] = [{
            lbPairAddress: pairDayData?.pairAddress,
            lbPairName: `${pairDayData.token0.name}-${pairDayData.token1.name}`,
            feeUSD: pairDayData.dailyVolumeUSD * TOTAL_FEE,
          }]
        }
      })
      poolFeeDataContainer.data = poolFeeData
      poolFeeDataContainer.pools = Array.from(pools.values())
      return poolFeeDataContainer
    } else {
      return {error: false} as PoolFeeDataContainer
    }
  })


export const fetchPoolsPublicDataAsync = (chainId: number, currentBlockTime: number) => async (dispatch, getState) => {
  const blockLimits = await fetchPoolsBlockLimits(chainId)
  const totalStakings = await fetchPoolsTotalStaking(chainId)

  const prices = getTokenPricesFromFarm(getState().farms.data[chainId])

  const liveData = poolsConfig[chainId].map(pool => toSerializedPoolConfig(chainId, pool)).map((pool) => {
    const blockLimit = blockLimits.find((entry) => entry.sousId === pool.sousId)
    const totalStaking = totalStakings.find((entry) => entry.sousId === pool.sousId)
    const isPoolEndBlockExceeded = currentBlockTime > 0 && blockLimit ? currentBlockTime > Number(blockLimit.endTime) : false
    const isPoolFinished = pool.isFinished || isPoolEndBlockExceeded

    const stakingTokenAddress = pool.stakingToken.address ? pool.stakingToken.address.toLowerCase() : null
    const stakingTokenPrice = stakingTokenAddress ? prices[stakingTokenAddress] : 0

    const earningTokenAddress = pool.earningToken.address ? pool.earningToken.address.toLowerCase() : null
    const earningTokenPrice = earningTokenAddress ? prices[earningTokenAddress] : 0
    const apr = !isPoolFinished
      ? getPoolApr(
        stakingTokenPrice,
        earningTokenPrice,
        getBalanceNumber(new BigNumber(totalStaking.totalStaked), pool.stakingToken.decimals),
        parseFloat(pool.tokenPerSecond),
      )
      : 0

    return {
      ...blockLimit,
      ...totalStaking,
      stakingTokenPrice,
      earningTokenPrice,
      apr,
      isFinished: isPoolFinished,
    }
  })

  dispatch(setPoolsPublicData({chainId, poolsData: liveData}))
}

export const fetchPoolsStakingLimitsAsync = (chainId) => async (dispatch, getState) => {
  const poolsWithStakingLimit = getState()
    .pools.data[chainId].filter(({stakingLimit}) => stakingLimit !== null && stakingLimit !== undefined)
    .map((pool) => pool.sousId)

  const stakingLimits = await fetchPoolsStakingLimits(chainId, poolsWithStakingLimit)

  const stakingLimitData = poolsConfig[chainId].map((pool) => {
    if (poolsWithStakingLimit.includes(pool.sousId)) {
      return {sousId: pool.sousId}
    }
    const stakingLimit = stakingLimits[pool.sousId] || BIG_ZERO
    return {
      sousId: pool.sousId,
      stakingLimit: stakingLimit.toJSON(),
    }
  })

  dispatch(setPoolsPublicData({chainId, poolsData: stakingLimitData}))
}

export const fetchPoolsUserDataAsync =
  (chainId: number, account: string): AppThunk =>
    async (dispatch) => {
      const allowances = await fetchPoolsAllowance(chainId, account)
      const stakingTokenBalances = await fetchUserBalances(chainId, account)
      const stakedBalances = await fetchUserStakeBalances(chainId, account)
      const pendingRewards = await fetchUserPendingRewards(chainId, account)

      const userData = poolsConfig[chainId].map((pool) => ({
        sousId: pool.sousId,
        allowance: allowances[pool.sousId],
        stakingTokenBalance: stakingTokenBalances[pool.sousId],
        stakedBalance: stakedBalances[pool.sousId],
        pendingReward: pendingRewards[pool.sousId],
      }))

      dispatch(setPoolsUserData({chainId, userData}))
    }

export const updateUserAllowance =
  (chainId: number, sousId: number, account: string): AppThunk =>
    async (dispatch) => {
      const allowances = await fetchPoolsAllowance(chainId, account)
      dispatch(updatePoolsUserData({chainId, sousId, field: 'allowance', value: allowances[sousId]}))
    }

export const updateUserBalance =
  (chainId: number, sousId: number, account: string): AppThunk =>
    async (dispatch) => {
      const tokenBalances = await fetchUserBalances(chainId, account)
      dispatch(updatePoolsUserData({chainId, sousId, field: 'stakingTokenBalance', value: tokenBalances[sousId]}))
    }

export const updateUserStakedBalance =
  (chainId: number, sousId: number, account: string): AppThunk =>
    async (dispatch) => {
      const stakedBalances = await fetchUserStakeBalances(chainId, account)
      dispatch(updatePoolsUserData({chainId, sousId, field: 'stakedBalance', value: stakedBalances[sousId]}))
    }

export const updateUserPendingReward =
  (chainId: number, sousId: number, account: string): AppThunk =>
    async (dispatch) => {
      const pendingRewards = await fetchUserPendingRewards(chainId, account)
      dispatch(updatePoolsUserData({chainId, sousId, field: 'pendingReward', value: pendingRewards[sousId]}))
    }

export const fetchCakeVaultPublicData = createAsyncThunk<CakeVault>('cakeVault/fetchPublicData', async () => {
  const publicVaultInfo = await fetchPublicVaultData()
  return publicVaultInfo
})

export const fetchCakeVaultFees = createAsyncThunk<VaultFees>('cakeVault/fetchFees', async () => {
  const vaultFees = await fetchVaultFees()
  return vaultFees
})

/*
export const fetchCakeVaultUserData = createAsyncThunk<VaultUser, { account: string }>(
  'cakeVault/fetchUser',
  async ({ account }) => {
    const userData = await fetchVaultUser(account)
    return userData
  },
) */

export const PoolsSlice = createSlice({
  name: 'Pools',
  initialState,
  reducers: {
    setPoolsPublicData: (state, action) => {
      const {poolsData, chainId} = action.payload
      state.data[chainId] = state.data[chainId].map((pool) => {
        const livePoolData = poolsData.find((entry) => entry.sousId === pool.sousId)
        return {...pool, ...livePoolData}
      })
    },
    setPoolsUserData: (state, action) => {
      const {userData, chainId} = action.payload
      state.data[chainId] = state.data[chainId].map((pool) => {
        const userPoolData = userData.find((entry) => entry.sousId === pool.sousId)
        return {...pool, userData: userPoolData}
      })
      state.userDataLoaded = true
    },
    updatePoolsUserData: (state, action) => {
      const {field, value, sousId, chainId} = action.payload
      const index = state.data[chainId].findIndex((p) => p.sousId === sousId)

      if (index >= 0) {
        state.data[chainId][index] = {...state.data[chainId][index], userData: {...state.data[chainId][index].userData, [field]: value}}
      }
    },
    resetPoolsFeeData: (state, action) => {
      state.poolFees = {
        ...state.poolFees,
        dataLoaded: false,
        byDate: {},
        pools: [],
      };
    },
  },
  extraReducers: (builder) => {
    // Vault public data that updates frequently
    builder.addCase(fetchCakeVaultPublicData.fulfilled, (state, action: PayloadAction<CakeVault>) => {
      state.cakeVault = {...state.cakeVault, ...action.payload}
    })
    // Vault fees
    builder.addCase(fetchCakeVaultFees.fulfilled, (state, action: PayloadAction<VaultFees>) => {
      const fees = action.payload
      state.cakeVault = {...state.cakeVault, fees}
    })
    builder.addCase(fetchAllPoolFeesAsync.fulfilled, (state, {payload: {data, pools}}) => {
      Object.keys(data).forEach(date => {
        state.poolFees.byDate[date] = data[date]
      })
      state.poolFees = {
        ...state.poolFees,
        dataLoaded: true,
        pools: pools
      }
    })

    // Vault user data
    /* builder.addCase(fetchCakeVaultUserData.fulfilled, (state, action: PayloadAction<VaultUser>) => {
      const userData = action.payload
      userData.isLoading = false
      state.cakeVault = { ...state.cakeVault, userData }
    }) */
  },
})

// Actions
export const {resetPoolsFeeData, setPoolsPublicData, setPoolsUserData, updatePoolsUserData} = PoolsSlice.actions

export default PoolsSlice.reducer
